import { useQuery, useMutation, useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { toast } from "@/hooks/useToast";

export type EmissionsProvider = "climatiq" | "local";
type EmissionsOperation = "search" | "estimate";

// Hard ceiling on paginated search pages — a runaway-pagination backstop set far
// above any real query depth. Normal termination comes from Climatiq's
// `last_page`/empty-page guards in getNextPageParam; this only catches pathology.
const MAX_SEARCH_PAGES = 25;

interface EmissionsProxyResponse<T = Record<string, unknown>> {
  data: T | null;
  meta: {
    cached: boolean;
    stale: boolean;
    fetched_at?: string;
    provider_version?: string;
    provider?: string;
  };
}

interface SearchResultData {
  results: Record<string, unknown>[];
  total: number;
  /** Climatiq's authoritative pagination cursor (absent for the local provider). */
  current_page?: number;
  last_page?: number;
  source: string;
  /** Climatiq search facets (available years/regions/etc.) — drives dynamic
   *  selectors in the picker. Absent for the local provider. */
  possible_filters?: {
    year?: number[];
    region?: { id: string; name: string }[];
    [k: string]: unknown;
  };
}

interface EstimateResultData {
  co2e: number;
  co2e_unit: string;
  source: string;
  details?: Record<string, unknown>;
}

async function invokeProxy<T>(
  organizationId: string,
  provider: EmissionsProvider,
  operation: EmissionsOperation,
  params: Record<string, unknown>
): Promise<EmissionsProxyResponse<T>> {
  const { data, error } = await supabase.functions.invoke("emissions-proxy", {
    body: {
      organization_id: organizationId,
      provider,
      operation,
      params,
    },
  });

  if (error) {
    const message =
      (data as { error?: string } | null)?.error ??
      error.message ??
      "Emissions proxy request failed";
    throw new Error(message);
  }

  // Separate _meta from the response payload
  const { _meta, ...rest } = data as Record<string, unknown> & {
    _meta?: Record<string, unknown>;
  };

  return {
    data: rest as T,
    meta: {
      cached: (_meta?.cached as boolean) ?? false,
      stale: (_meta?.stale as boolean) ?? false,
      fetched_at: _meta?.fetched_at as string | undefined,
      provider_version: _meta?.provider_version as string | undefined,
      provider: _meta?.provider as string | undefined,
    },
  };
}

/**
 * Search emission factors from a provider.
 *
 * Uses TanStack Query with a 5-minute client-side stale time
 * (server-side cache is 30d for Climatiq, 90d for local).
 *
 * Note: `params` should be memoized by the caller (useMemo) to avoid
 * unnecessary refetches on re-render.
 */
export function useEmissionsSearch({
  provider,
  params,
  enabled = true,
}: {
  provider: EmissionsProvider;
  params: Record<string, unknown>;
  enabled?: boolean;
}) {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  return useQuery<EmissionsProxyResponse<SearchResultData>>({
    // Use serialized params for stable cache key across re-renders
    queryKey: ["emissions-proxy", "search", provider, JSON.stringify(params)],
    queryFn: () =>
      invokeProxy<SearchResultData>(orgId!, provider, "search", params),
    enabled: !!orgId && enabled,
    staleTime: 5 * 60 * 1000, // 5 min client-side
  });
}

/**
 * Paginated search for the factor picker's infinite scroll. Climatiq paginates
 * via `page` (total_results drives "is there more?"); the local provider has no
 * pagination, so it always returns a single page. Callers flatten
 * `data.pages.flatMap(p => p.data.results)`.
 */
export function useEmissionsSearchInfinite({
  provider,
  params,
  enabled = true,
  pageSize = 25,
}: {
  provider: EmissionsProvider;
  params: Record<string, unknown>;
  enabled?: boolean;
  pageSize?: number;
}) {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  return useInfiniteQuery<EmissionsProxyResponse<SearchResultData>>({
    queryKey: [
      "emissions-proxy",
      "search-infinite",
      provider,
      pageSize,
      JSON.stringify(params),
    ],
    queryFn: ({ pageParam }) => {
      const pageParams =
        provider === "climatiq"
          ? { results_per_page: pageSize, page: pageParam }
          : { limit: pageSize };
      return invokeProxy<SearchResultData>(orgId!, provider, "search", {
        ...params,
        ...pageParams,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      // Local provider isn't paginated — one page only.
      if (provider !== "climatiq") return undefined;

      // Loop guard 1 — an empty page means we've run past the real end, even if
      // total_results still claims more. Stop rather than cycling empty pages
      // forever (the classic infinite-pagination trap when total over-counts).
      const pageResults = lastPage.data?.results ?? [];
      if (pageResults.length === 0) return undefined;

      // Loop guard 2 — absolute backstop so no pathological response can drive
      // unbounded fetching.
      if (allPages.length >= MAX_SEARCH_PAGES) return undefined;

      // Authoritative end signal — Climatiq tells us the last page directly;
      // stop once we've fetched it.
      const current = lastPage.data?.current_page;
      const last = lastPage.data?.last_page;
      if (typeof current === "number" && typeof last === "number") {
        return current < last ? current + 1 : undefined;
      }

      // Fallback when the cursor is absent — infer from total_results.
      const total = lastPage.data?.total ?? 0;
      const loaded = allPages.reduce(
        (n, p) => n + (p.data?.results?.length ?? 0),
        0,
      );
      return loaded < total ? allPages.length + 1 : undefined;
    },
    enabled: !!orgId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Estimate emissions via a provider.
 *
 * Returns a mutation that can be triggered on demand.
 */
export function useEmissionsEstimate() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  return useMutation<
    EmissionsProxyResponse<EstimateResultData>,
    Error,
    {
      provider: EmissionsProvider;
      params: Record<string, unknown>;
    }
  >({
    mutationFn: ({ provider, params }) => {
      if (!orgId) {
        return Promise.reject(new Error("No organization selected"));
      }
      return invokeProxy<EstimateResultData>(orgId, provider, "estimate", params);
    },
    onError: () => {
      toast.errorTemplates.generic();
    },
  });
}
